import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Facebook } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", country: "", message: "" };

export default function InquiryForm() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, interest: "columbarium" });
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again or message us on Facebook."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-[#0C2340]/15 bg-white px-4 py-3 text-sm text-[#0C2340] placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#C59B27]/60 focus:border-[#C59B27] transition";

  return (
    <section id="inquire" className="py-20 sm:py-28 px-4 sm:px-8 bg-[#F5EFDE]/60">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C59B27]">Columbarium Inquiry</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0C2340] mt-4 leading-tight">
            Secure a niche at In Aeternum — even from abroad
          </h2>
          <p className="mt-6 text-base text-zinc-600 leading-relaxed">
            Leave your name, email, and contact number below, and a DMPI staff
            member will reach out to you with niche availability, pricing, and
            payment options for overseas Filipino workers.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-zinc-600">
            <li>• No obligation — just a friendly conversation with our team</li>
            <li>• We assist via email, Messenger, or WhatsApp, in any time zone</li>
            <li>• Family members in Davao can also visit the Park on your behalf</li>
          </ul>
        </div>

        <div className="bg-white rounded-2xl border border-[#0C2340]/10 p-6 sm:p-8 shadow-[0_4px_20px_rgba(12,35,64,0.06)]">
          {submitted ? (
            <div data-testid="inquiry-success-panel" className="text-center py-10">
              <CheckCircle2 className="w-14 h-14 text-[#2E5A44] mx-auto" />
              <h3 className="font-display text-2xl font-semibold text-[#0C2340] mt-5">
                Thank you, {form.name.split(" ")[0]}!
              </h3>
              <p className="mt-3 text-sm text-zinc-600 leading-relaxed max-w-sm mx-auto">
                Your inquiry about the In Aeternum Columbarium has been received.
                A DMPI staff member will contact you soon at{" "}
                <span className="font-medium text-[#0C2340]">{form.email}</span>.
              </p>
              <a
                href="https://www.facebook.com/DavaoMemorialPark"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="inquiry-success-facebook-link"
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#0C2340] border border-[#0C2340]/20 px-6 py-2.5 rounded-full hover:bg-[#0C2340] hover:text-white transition-colors"
              >
                <Facebook className="w-4 h-4" />
                Message us faster on Facebook
              </a>
              <div>
                <button
                  data-testid="inquiry-another-button"
                  onClick={() => { setForm(EMPTY); setSubmitted(false); }}
                  className="mt-4 text-xs text-zinc-500 underline hover:text-[#C59B27]"
                >
                  Send another inquiry
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="columbarium-inquiry-form">
              <div>
                <label htmlFor="name" className="block text-xs font-semibold text-[#0C2340] mb-1.5">Full Name *</label>
                <input id="name" name="name" required value={form.name} onChange={update} placeholder="Juan Dela Cruz" className={inputCls} data-testid="inquiry-form-name-input" />
              </div>
              <div>
                <label htmlFor="email" className="block text-xs font-semibold text-[#0C2340] mb-1.5">Email Address *</label>
                <input id="email" name="email" type="email" required value={form.email} onChange={update} placeholder="juan@email.com" className={inputCls} data-testid="inquiry-form-email-input" />
              </div>
              <div>
                <label htmlFor="phone" className="block text-xs font-semibold text-[#0C2340] mb-1.5">Contact Number (WhatsApp / Viber) *</label>
                <input id="phone" name="phone" required value={form.phone} onChange={update} placeholder="+63 917 000 0000" className={inputCls} data-testid="inquiry-form-phone-input" />
              </div>
              <div>
                <label htmlFor="country" className="block text-xs font-semibold text-[#0C2340] mb-1.5">Country Where You Work (optional)</label>
                <input id="country" name="country" value={form.country} onChange={update} placeholder="e.g. Saudi Arabia, USA, Singapore" className={inputCls} data-testid="inquiry-form-country-input" />
              </div>
              <div>
                <label htmlFor="message" className="block text-xs font-semibold text-[#0C2340] mb-1.5">Message (optional)</label>
                <textarea id="message" name="message" rows={3} value={form.message} onChange={update} placeholder="I'd like to know about niche pricing and payment terms…" className={inputCls} data-testid="inquiry-form-message-input" />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="inquiry-form-submit-button"
                className="w-full bg-[#0C2340] hover:bg-[#091A30] text-white font-semibold py-3.5 rounded-xl transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting ? "Sending…" : "Send My Inquiry"}
              </button>
              <p className="text-xs text-zinc-500 text-center">
                A DMPI staff member will contact you after you submit.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
